import 'core-js-pure/stable';
import 'regenerator-runtime/runtime';

import { Container, Typography, Paper } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';

import Copyright from '../../components/Copyright';
import PrimaryAppBar from '../../components/PrimaryAppBar';
import Settings from '../../components/Settings';
import AccountManagement from '../account-management/AccountManagement';
import Home from '../home/Home';
import MerchantOnboard from '../merchant-onboarding/MerchantOnboarding';
import UserManagement from '../user-management/UserManagement';
import ChallengeDispute from '../challenge-dispute/ChallengeDispute';
import { authProvider } from '../../util/AuthProvider';
import { WEPAY_API_VERSION, WEPAY_APP_ID, WEPAY_ENVIRONMENT } from '../../util/Constants';
import { WePayInvalidParamsError } from '../../util/WePay';
import { fetchViewerUserByEmail, fetchTenantAccount } from './AppActions';
import {
  selectAppError,
  selectTenantAccount,
  selectViewerUser,
  selectNetworkBusy,
  selectUserNoEmail,
  fetchUserNoEmail,
} from './AppSlice';
import Help from '../help/Help';

interface AppProps {
  wepayInit: (environment: string, appId: string, apiVersion: string) => WePayInvalidParamsError | void | undefined;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      padding: theme.spacing(2),
      textAlign: 'center',
      color: theme.palette.text.secondary,
    },
    root: {
      minHeight: '100%',
      width: '100%',
      maxWidth: '100%',
      margin: 0,
      padding: 0,
    },
    canvas: {
      maxWidth: '100%',
      display: 'flex',
      justifyContent: 'center',
      [theme.breakpoints.up('md')]: {
        margin: theme.spacing(3, 0),
      },
      [theme.breakpoints.down('md')]: {
        margin: theme.spacing(1.5, 0),
      },
      [theme.breakpoints.down('lg')]: {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
      },
    },
    hyperlinktext: {
      color: theme.palette.common.white,
    },
  }),
);

const App: React.FC<AppProps> = props => {
  const { wepayInit } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const tenantAccount = useSelector(selectTenantAccount);
  const viewerUser = useSelector(selectViewerUser);
  const networkBusy = useSelector(selectNetworkBusy);
  const appError = useSelector(selectAppError);
  const userNoEmail = useSelector(selectUserNoEmail);

  useEffect(() => {
    // initialize wepay sdk
    wepayInit(WEPAY_ENVIRONMENT, WEPAY_APP_ID, WEPAY_API_VERSION);

    // fetch user info
    authProvider.getIdToken().then(val => {
      console.log('[0] fetching user');
      const email = val.idToken.claims.emails ? val.idToken.claims.emails[0] : null;
      if (email) {
        dispatch(fetchViewerUserByEmail(email));
      } else {
        dispatch(fetchUserNoEmail(true));
      }
    });
  }, []);

  useEffect(() => {
    // Fetch addition data.
    if (viewerUser) {
      dispatch(fetchTenantAccount());
    }
  }, [viewerUser]);
  const isOnboarded = !!tenantAccount?.tosAcceptance;
  const getAppContent = () => {
    if (userNoEmail === true) {
      return <Paper className={classes.paper}>Email not found. Contact administration.</Paper>;
    }
    if (appError) {
      // Error state.
      return <Paper className={classes.paper}>{!tenantAccount ? 'Unable to fetch tenant account!' : 'Error!'}</Paper>;
    }
    if ((!viewerUser || !tenantAccount?.tosAcceptance) && networkBusy) {
      // If no viewerUser or merchant account and the network is busy, show loading.
      return <Paper className={classes.paper}>Loading!</Paper>;
    }
    if (isOnboarded) {
      return (
        <Switch>
          <Route path="/settings">
            <Settings />
          </Route>
          <Route path="/account-management">
            <AccountManagement />
          </Route>
          <Route path="/user-management">
            <UserManagement />
          </Route>
          <Route path="/challenge-dispute">
            <ChallengeDispute />
          </Route>
          <Route path="/help">
            <Help />
          </Route>
          <Route path="/">
            <Home />
          </Route>
        </Switch>
      );
    }
    return <MerchantOnboard />;
  };

  return (
    <Container className={classes.root}>
      <Typography variant="h4" component="h1">
        <PrimaryAppBar networkBusy={networkBusy} isOnboarded={isOnboarded} viewerUser={viewerUser} />
      </Typography>
      <Container className={classes.canvas}>{getAppContent()}</Container>
      <Copyright />
    </Container>
  );
};

export default App;
